<script lang="ts">
  /**
   * One option of a picker, as a card: a glyph (four rising bars lit to a
   * level, or nothing), a bold name, the promise it makes, up to two quieter
   * lines, and a ring at the right that fills when it is the chosen one.
   * Chosen is a 2px accent border and a 10% wash of the accent. TwinTen's
   * level card.
   *
   * It is a `radio` inside `RadioCards` (exactly one chosen), or a `toggle`
   * on its own (a loadout: several may be chosen), where it is a pressed
   * button instead.
   *
   * Named by its `name` and described by its other lines.
   *
   * Since 1.7.0: `lang`, the language the name is written in (set on the
   * name); `avatar`, a person's face before the name (a URL, or `null` for
   * the name's initial), decorative since the name is beside it.
   *
   * Since 1.8.0:
   *
   * - `richName`: the name as the game draws it (code, a formula), in place
   *   of `name` on screen and hidden from assistive technology; `name` stays
   *   the card's accessible name (written out where the drawing does not
   *   read well).
   * - `tone`, a graded card once the answer is known: `right` or `wrong`.
   *   Never colour alone: a check or a cross in place of the ring (in a
   *   filled disc on the chosen card, as the ring fills when chosen), the
   *   outline in `--ui-good` or `--ui-danger` (both gated on the card and
   *   on a chosen card's wash), and `toneLabel` ("Right answer", the shell's
   *   `choiceRight` / `choiceWrong` by default) at the end of its name for a
   *   screen reader. `data-tone` on the card. A graded card stays pressable
   *   unless the game disables it (a faded card would fade the marks too:
   *   ignore the presses instead).
   *
   * - `detailLang`: the language of the lines under the name (the tagline,
   *   the detail, the extra line), set as `lang` on each. `lang` stays the
   *   name's alone, so a card whose name is in another language and whose
   *   lines are in the page's is still right; a language card passes both
   *   ("Nederlands" over "299.975 woorden · OpenTaal").
   *
   * Test ids: `testid` on the card, `taglineTestid` on the tagline. No defaults.
   */
  import type { Snippet } from 'svelte';

  import { shellText } from '../i18n.svelte.js';
  import type { ChoiceTone } from '../types.js';
  import Avatar from './Avatar.svelte';
  import Icon from './Icon.svelte';

  interface Props {
    readonly kind?: 'radio' | 'toggle';
    readonly selected: boolean;
    readonly name: string;
    /** The promise the option makes: one line, in ink. */
    readonly tagline?: string | undefined;
    /** A quieter line of numbers or detail. */
    readonly detail?: string | undefined;
    /** A fourth line, as quiet as the detail. */
    readonly extra?: string | undefined;
    /** How many of the four rising bars are lit, 1 to 4. Absent: no meter. */
    readonly meter?: number | undefined;
    /** A small uppercase tag after the name. */
    readonly tag?: string | undefined;
    readonly disabled?: boolean;
    /** Set by `RadioCards`: the group's one Tab stop. */
    readonly tabindex?: 0 | -1 | undefined;
    readonly onclick: () => void;
    readonly onkeydown?: ((event: KeyboardEvent) => void) | undefined;
    readonly testid?: string | undefined;
    readonly taglineTestid?: string | undefined;
    // ── 1.7.0 ──
    /** The language the name is written in, when it is not the page's. */
    readonly lang?: string | undefined;
    /** A person's face before the name: a URL, or `null` for the initial. */
    readonly avatar?: string | null | undefined;
    // ── 1.8.0 ──
    /** The name as the game draws it (code, a formula); `name` stays its accessible name. */
    readonly richName?: Snippet | undefined;
    /** Graded: the right answer or a wrong one (a glyph, an outline and a word). */
    readonly tone?: ChoiceTone | undefined;
    /** The word a screen reader hears for `tone`; the shell's own by default. */
    readonly toneLabel?: string | undefined;
    /** The language of the lines under the name (tagline, detail, extra), when not the page's. */
    readonly detailLang?: string | undefined;
  }
  const {
    kind = 'radio',
    selected,
    name,
    tagline,
    detail,
    extra,
    meter,
    tag,
    disabled = false,
    tabindex,
    onclick,
    onkeydown,
    testid,
    taglineTestid,
    lang,
    avatar,
    richName,
    tone,
    toneLabel,
    detailLang,
  }: Props = $props();

  const toneWord = $derived(
    tone === undefined
      ? undefined
      : (toneLabel ?? shellText(tone === 'right' ? 'choiceRight' : 'choiceWrong')),
  );

  const BARS = [1, 2, 3, 4] as const;
  const uid = $props.id();
  const described = $derived(tagline !== undefined || detail !== undefined || extra !== undefined);
</script>

<button
  type="button"
  class="ui-choice-card"
  class:ui-choice-card--active={selected}
  class:ui-choice-card--right={tone === 'right'}
  class:ui-choice-card--wrong={tone === 'wrong'}
  data-tone={tone}
  role={kind === 'radio' ? 'radio' : undefined}
  aria-checked={kind === 'radio' ? selected : undefined}
  aria-pressed={kind === 'toggle' ? selected : undefined}
  aria-labelledby={`${uid}-name`}
  aria-describedby={described ? `${uid}-more` : undefined}
  {disabled}
  {tabindex}
  data-testid={testid}
  {onclick}
  {onkeydown}
>
  {#if meter !== undefined}
    <span class="ui-choice-meter" aria-hidden="true">
      {#each BARS as bar (bar)}
        <i class:ui-choice-meter-bar--on={bar <= meter}></i>
      {/each}
    </span>
  {/if}
  {#if avatar !== undefined}
    <Avatar {name} src={avatar} size="sm" tone="surface" />
  {/if}
  <span class="ui-choice-text">
    {#if richName === undefined && toneWord === undefined}
      <span class="ui-choice-name" id={`${uid}-name`}
        >{#if lang !== undefined}<span {lang}>{name}</span
          >{:else}{name}{/if}{#if tag !== undefined}<span class="ui-choice-tag">{tag}</span
          >{/if}</span
      >
    {:else}
      <!-- 1.8.0: a drawn name (hidden from assistive technology, `name` said
           in its place) and a graded card's word, said at the end. -->
      <span class="ui-choice-name" id={`${uid}-name`}
        >{#if richName !== undefined}<span class="ui-choice-sr" {lang}>{name}</span><span
            class="ui-choice-rich"
            aria-hidden="true">{@render richName()}</span
          >{:else if lang !== undefined}<span {lang}>{name}</span
          >{:else}{name}{/if}{#if tag !== undefined}<span class="ui-choice-tag">{tag}</span
          >{/if}{#if toneWord !== undefined}<span class="ui-choice-sr">, {toneWord}</span
          >{/if}</span
      >
    {/if}
    <span class="ui-choice-more" id={`${uid}-more`}>
      {#if tagline !== undefined}
        <span class="ui-choice-tagline" lang={detailLang} data-testid={taglineTestid}
          >{tagline}</span
        >
      {/if}
      {#if detail !== undefined}
        <span class="ui-choice-detail" lang={detailLang}>{detail}</span>
      {/if}
      {#if extra !== undefined}
        <span class="ui-choice-detail" lang={detailLang}>{extra}</span>
      {/if}
    </span>
  </span>
  {#if tone !== undefined}
    <span
      class="ui-choice-grade ui-choice-grade--{tone}"
      class:ui-choice-grade--chosen={selected}
      aria-hidden="true"><Icon name={tone === 'right' ? 'check' : 'close'} size={14} /></span
    >
  {:else}
    <span class="ui-choice-ring" class:ui-choice-ring--on={selected} aria-hidden="true"></span>
  {/if}
</button>

<style>
  .ui-choice-card {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    min-height: 2.75rem;
    border: 2px solid var(--ui-line);
    border-radius: var(--ui-radius-md);
    padding: 0.65rem 0.85rem;
    background: var(--ui-card);
    color: var(--ui-ink);
    font: var(--ui-text-body);
    text-align: left;
  }

  .ui-choice-card--active {
    border-color: var(--ui-accent);
    background: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-card));
  }

  .ui-choice-card:disabled {
    opacity: 0.5;
  }

  .ui-choice-card:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-choice-meter {
    flex: 0 0 auto;
    display: flex;
    align-items: flex-end;
    gap: 0.2rem;
    width: 1.6rem;
    height: 1.4rem;
  }

  .ui-choice-meter i {
    flex: 1;
    height: 25%;
    border-radius: 2px;
    background: var(--ui-muted);
  }

  .ui-choice-meter i:nth-child(2) {
    height: 50%;
  }

  .ui-choice-meter i:nth-child(3) {
    height: 75%;
  }

  .ui-choice-meter i:nth-child(4) {
    height: 100%;
  }

  .ui-choice-meter i.ui-choice-meter-bar--on {
    background: var(--ui-accent);
  }

  .ui-choice-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-choice-more {
    display: contents;
  }

  .ui-choice-name {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-choice-tag {
    font: var(--ui-text-eyebrow);
    letter-spacing: 0.06em;
    padding: 0.1rem 0.4rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-sunken);
    color: var(--ui-accent);
    text-transform: uppercase;
  }

  .ui-choice-tagline {
    font: var(--ui-text-label);
    font-weight: 400;
    line-height: 1.3;
    color: var(--ui-ink);
  }

  .ui-choice-detail {
    font: var(--ui-text-eyebrow);
    font-weight: 400;
    color: var(--ui-ink-2);
  }

  .ui-choice-ring {
    flex: 0 0 auto;
    width: 1.15rem;
    height: 1.15rem;
    border-radius: 50%;
    border: 2px solid var(--ui-muted);
  }

  .ui-choice-ring--on {
    border: 6px solid var(--ui-accent);
  }

  /* 1.8.0: a drawn name sits where the name's words would, at their size. */
  .ui-choice-rich {
    display: inline-flex;
    align-items: baseline;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  /* Said, not seen: the name behind a drawn one, a graded card's word. */
  .ui-choice-sr {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* A graded card (1.8.0): the outline in the tone, a check or a cross in a
     ring of the same colour where the choice ring was. Both colours are
     gated as graphics on the card and on a chosen card's wash. */
  .ui-choice-card--right {
    border-color: var(--ui-good);
  }

  .ui-choice-card--wrong {
    border-color: var(--ui-danger);
  }

  .ui-choice-grade {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 50%;
    border: 2px solid currentColor;
    box-sizing: border-box;
  }

  .ui-choice-grade--right {
    color: var(--ui-good);
  }

  .ui-choice-grade--wrong {
    color: var(--ui-danger);
  }

  /* The graded card that was chosen: its disc filled, as the choice ring
     fills, so being the chosen one is a shape as well as the wash. The
     glyph on the fill is a gated pair: on-accent on good (a result's delta
     pill), on-danger on danger (a danger button). */
  .ui-choice-grade--chosen.ui-choice-grade--right {
    border-color: var(--ui-good);
    background: var(--ui-good);
    color: var(--ui-on-accent);
  }

  .ui-choice-grade--chosen.ui-choice-grade--wrong {
    border-color: var(--ui-danger);
    background: var(--ui-danger);
    color: var(--ui-on-danger);
  }
</style>
